import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Paper, CircularProgress, Snackbar, Alert,
  IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../components/PageHeader';
import { getRooms, createRoom, updateRoom, deleteRoom } from '../services/api';

const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentRoom, setCurrentRoom] = useState({
    room_name: '',
    capacity: '',
    room_type: ''
  });
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const columns = [
    { field: 'room_id', headerName: 'ID', width: 90 },
    { field: 'room_name', headerName: 'Room Name', flex: 1 },
    { field: 'capacity', headerName: 'Capacity', width: 120 },
    { field: 'room_type', headerName: 'Room Type', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton 
            color="primary" 
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            size="small"
            onClick={() => handleDeleteClick(params.row.room_id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await getRooms();
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load rooms',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setCurrentRoom({
      room_name: '',
      capacity: '',
      room_type: ''
    });
    setSelectedId(null);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEdit = (room) => {
    setCurrentRoom({
      room_name: room.room_name,
      capacity: room.capacity,
      room_type: room.room_type
    });
    setSelectedId(room.room_id);
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteRoom(selectedId);
      setRooms(rooms.filter(room => room.room_id !== selectedId));
      setSnackbar({
        open: true,
        message: 'Room deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting room:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete room',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRoom({ ...currentRoom, [name]: value });
    
    // Clear the error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentRoom.room_name.trim()) {
      errors.room_name = 'Room name is required';
    }
    if (!currentRoom.capacity) {
      errors.capacity = 'Capacity is required';
    } else if (isNaN(currentRoom.capacity) || currentRoom.capacity <= 0) {
      errors.capacity = 'Capacity must be a positive number';
    }
    if (!currentRoom.room_type) {
      errors.room_type = 'Room type is required';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      // Ensure capacity is a number
      const roomData = {
        ...currentRoom,
        capacity: parseInt(currentRoom.capacity, 10)
      };

      if (selectedId) {
        // Update existing room
        await updateRoom(selectedId, roomData);
        setRooms(rooms.map(room => 
          room.room_id === selectedId ? { ...room, ...roomData, room_id: selectedId } : room
        ));
        setSnackbar({
          open: true,
          message: 'Room updated successfully',
          severity: 'success'
        });
      } else {
        // Create new room
        const response = await createRoom(roomData);
        const newRoom = {
          ...roomData,
          room_id: response.data.room_id
        };
        setRooms([...rooms, newRoom]);
        setSnackbar({
          open: true,
          message: 'Room created successfully',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving room:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save room',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <PageHeader 
        title="Rooms" 
        subtitle="Manage college rooms" 
        action={
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Room
          </Button>
        }
      />

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={rooms}
            columns={columns}
            getRowId={(row) => row.room_id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            checkboxSelection={false}
            disableSelectionOnClick
            autoHeight
          />
        )}
      </Paper>

      {/* Add/Edit Room Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Room' : 'Add Room'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              name="room_name"
              label="Room Name"
              fullWidth
              margin="normal"
              value={currentRoom.room_name}
              onChange={handleInputChange}
              error={!!formErrors.room_name}
              helperText={formErrors.room_name}
            />
            <TextField
              name="capacity"
              label="Capacity"
              type="number"
              fullWidth
              margin="normal"
              value={currentRoom.capacity}
              onChange={handleInputChange}
              error={!!formErrors.capacity}
              helperText={formErrors.capacity}
              inputProps={{ min: 1 }}
            />
            <FormControl fullWidth margin="normal" error={!!formErrors.room_type}>
              <InputLabel>Room Type</InputLabel>
              <Select
                name="room_type"
                value={currentRoom.room_type}
                label="Room Type"
                onChange={handleInputChange}
              >
                <MenuItem value="Lecture Hall">Lecture Hall</MenuItem>
                <MenuItem value="Lab">Lab</MenuItem>
                <MenuItem value="Seminar Room">Seminar Room</MenuItem>
              </Select>
              {formErrors.room_type && (
                <Box sx={{ color: 'error.main', mt: 0.5, fontSize: '0.75rem' }}>
                  {formErrors.room_type}
                </Box>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this room? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Rooms;
